import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { token } from '@atlaskit/tokens';
import { isNativeEmbedEditorPlugin } from '@databases/modes';
import composeRefs from '@seznam/compose-react-refs';
import React, { forwardRef, type ReactNode, type Ref, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-left: ${() => (isNativeEmbedEditorPlugin() ? 0 : 1)}px;
  height: 100%;
`;

const overheadSpaceStyle = css`
  margin-top: ${token('space.negative.100', '-8px')};
  padding-top: ${token('space.100', '8px')};
`;

const hideScrollbarStyle = css`
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const InnerContainer = styled.div<{
  allowOverheadSpace?: boolean;
  heightIsLessThanContainer?: boolean;
  widthIsLessThanContainer?: boolean;
  hideScrollbar?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-x: ${(props) => (props.widthIsLessThanContainer ? 'hidden' : 'auto')};
  overflow-y: ${(props) => (props.heightIsLessThanContainer ? 'hidden' : 'auto')};
  margin-right: ${() => (isNativeEmbedEditorPlugin() ? 0 : 1)}px;
  padding-right: ${() => (isNativeEmbedEditorPlugin() ? 3 : 0)}px;

  ${(props) => props.hideScrollbar && hideScrollbarStyle}
  ${(props) => props.allowOverheadSpace && overheadSpaceStyle}
`;

interface ScrollableContainerWithShadowProps {
  className?: string;
  children: ReactNode;
  innerRef?: Ref<HTMLDivElement>;
  heightIsLessThanContainer?: boolean;
  widthIsLessThanContainer?: boolean;
  allowOverheadSpace?: boolean;
  hideScrollbar?: boolean;
  autoScrollAxis?: 'all' | 'horizontal' | 'vertical';
}

const ScrollableContainer = forwardRef<
  HTMLDivElement,
  ScrollableContainerWithShadowProps
>(function ScrollableContainer(
  {
    className,
    children,
    innerRef,
    heightIsLessThanContainer,
    widthIsLessThanContainer,
    allowOverheadSpace,
    hideScrollbar,
    autoScrollAxis = 'all'
  },
  ref
) {
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);

  // Enables auto scrolling for table fields
  useEffect(() => {
    if (!innerContainerRef.current) {
      return;
    }
    return combine(
      autoScrollForElements({
        element: innerContainerRef.current,
        getConfiguration: () => ({
          maxScrollSpeed: 'standard'
        }),
        getAllowedAxis: () => autoScrollAxis
      })
    );
  }, [autoScrollAxis]);

  return (
    <OuterContainer
      data-testid="scrollable-outer-container"
      ref={composeRefs(outerContainerRef, ref)}
      className={className}
    >
      <InnerContainer
        id={'scrollable-inner-container'}
        data-testid="scrollable-inner-container"
        data-canvas-target="database-scrollable-inner-container"
        allowOverheadSpace={allowOverheadSpace}
        ref={composeRefs(innerContainerRef, innerRef)}
        heightIsLessThanContainer={heightIsLessThanContainer}
        widthIsLessThanContainer={widthIsLessThanContainer}
        hideScrollbar={hideScrollbar}
      >
        {children}
      </InnerContainer>
    </OuterContainer>
  );
});

export default ScrollableContainer;
